import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineIndicator, setShowOfflineIndicator] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      console.log('[PWA] App is running in standalone mode');
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed the prompt before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const oneWeek = 7 * 24 * 60 * 60 * 1000;
      
      // Show banner if not dismissed or dismissed more than a week ago
      if (!dismissed || Date.now() - dismissedTime > oneWeek) {
        setTimeout(() => setShowInstallBanner(true), 3000); // Show after 3 seconds
      }
    };

    // Online/offline handling
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineIndicator(true);
      setTimeout(() => setShowOfflineIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineIndicator(true);
    };

    // Service worker update handling
    const handleSwUpdate = () => {
      setHasUpdate(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('sw-update-available', handleSwUpdate);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('sw-update-available', handleSwUpdate);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log('[PWA] Install prompt outcome:', outcome);
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
    
    if (outcome === 'dismissed') {
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleUpdate = () => {
    window.location.reload();
  };

  return (
    <>
      {/* Offline Indicator */}
      {showOfflineIndicator && (
        <div 
          className={`fixed top-0 left-0 right-0 z-50 py-2 px-4 text-center text-sm font-medium transition-all duration-300 ${
            isOnline 
              ? 'bg-green-500 text-white' 
              : 'bg-amber-500 text-white'
          }`}
          data-testid="offline-indicator"
        >
          <div className="flex items-center justify-center gap-2">
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4" />
                <span>You're back online</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4" />
                <span>You're offline - Showing cached data</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Update Available Banner */}
      {hasUpdate && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 py-3 px-4 bg-primary text-white"
          data-testid="update-banner"
        >
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <span className="text-sm font-medium">A new version is available!</span>
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={handleUpdate}
              className="bg-white text-primary hover:bg-white/90"
            >
              Update Now
            </Button>
          </div>
        </div>
      )}

      {/* Install Banner */}
      {showInstallBanner && deferredPrompt && (
        <div 
          className="fixed bottom-24 left-4 right-4 z-50 max-w-lg mx-auto animate-in slide-in-from-bottom-4 duration-300"
          data-testid="pwa-install-banner"
        >
          <div className="bg-card border border-border rounded-2xl p-4 shadow-elevated">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground mb-1">Install SnapTally</h3>
                <p className="text-sm text-muted-foreground">
                  Add to your home screen for quick access and offline support
                </p>
              </div>
              
              <button 
                onClick={handleDismiss}
                className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex gap-3 mt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleDismiss}
              >
                Not Now
              </Button>
              <Button 
                className="flex-1 gap-2"
                onClick={handleInstall}
              >
                <Download className="w-4 h-4" />
                Install
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
